import React, { ReactNode } from 'react'
import { LanguageSelector } from '../language-selector'
import { UserMenu } from '../user-menu'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { cn } from '../../lib/utils'
import { Menu, Moon, Sun } from 'lucide-react'
import { Button } from '../ui/button'
import { useTheme } from '../../lib/theme-context'

interface HeaderProps {
  sidebarOpen?: boolean
  setSidebarOpen?: (open: boolean) => void
  leftNode?: ReactNode
}

function ThemeToggle() {
  const { theme, setTheme } = useTheme()

  return (
    <Button variant="ghost" size="icon" onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}>
      <Sun className="h-5 w-5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
      <Moon className="absolute h-5 w-5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
      <span className="sr-only">Toggle theme</span>
    </Button>
  )
}

export function Header({ sidebarOpen, setSidebarOpen, leftNode }: HeaderProps) {
  const { t } = useTranslation()
  const location = useLocation()

  const navLinks = [{ href: '/', label: t('home') }]

  return (
    <header className="sticky top-0 z-50 flex w-full bg-white drop-shadow-sm dark:bg-gray-800 dark:drop-shadow-none">
      <div className="flex flex-grow items-center justify-between px-4 py-4 shadow-sm md:px-6 2xl:px-11">
        <div className="flex items-center gap-2 sm:gap-4">
          {leftNode}
          {/* Hamburger Toggle Button */}
          {setSidebarOpen && (
            <button
              aria-controls="sidebar"
              onClick={(e) => {
                e.stopPropagation()
                setSidebarOpen(!sidebarOpen)
              }}
              className="block rounded-sm border border-stroke bg-white p-1.5 shadow-sm dark:border-strokedark dark:bg-boxdark lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
          )}

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img
              src="https://pbs.twimg.com/profile_images/1285208896060129281/kpFksf-W_400x400.jpg"
              alt="Incept5 Logo"
              className="h-8 w-8 rounded-full"
            />
            <span className="hidden text-xl font-semibold text-black dark:text-white sm:block">{t('title')}</span>
          </Link>

          {/* Navigation Links - Desktop */}
          <nav className="hidden md:flex items-center gap-4 px-4">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={cn(
                  'text-sm font-medium transition-colors hover:text-primary',
                  location.pathname === link.href ? 'text-black dark:text-white' : 'text-black/60 dark:text-white/60',
                )}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>

        {/* Right side elements */}
        <div className="flex items-center gap-3 2xl:gap-7">
          <ThemeToggle />
          <LanguageSelector />
          <UserMenu />
        </div>
      </div>
    </header>
  )
}
